import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Grid,
  Card,
  CardActionArea,
  CardContent,
  Container,
  Stack,
  Divider
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import { ChemicalDetails, chemicalItems } from './chemicalData';
import { ChemicalDetailModal } from './ChemicalDetailModal';

export const ChemicalListComponent: React.FC = () => {
  const [selectedChemical, setSelectedChemical] = useState<ChemicalDetails | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const handleItemClick = (chemical: ChemicalDetails) => {
    setSelectedChemical(chemical);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedChemical(null);
  };

  const filteredItems = chemicalItems.filter(
    (item) =>
      item.chemicalName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.productName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.casNo.toLowerCase().includes(searchTerm.toLowerCase())
  );


  return (
    <Box
      sx={{
        py: { xs: 2, sm: 3 },
      }}
    >
      <Container maxWidth="lg">
        {/* Search Bar */}
        <Box sx={{ mb: 3, pt: 1 }}>
          <TextField
            fullWidth
            placeholder="Search chemical name, product name, or CAS number..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#94a3b8', fontSize: '20px' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                backgroundColor: '#ffffff',
                fontSize: '14px',
                transition: 'all 0.3s ease',
                boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                border: '1px solid #e2e8f0',
                '&:hover fieldset': {
                  borderColor: '#8ba7c7',
                  borderWidth: 1,
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#003883',
                  borderWidth: 1,
                  boxShadow: '0 0 0 3px rgba(0, 56, 131, 0.08)',
                },
              },
              '& .MuiOutlinedInput-input::placeholder': {
                color: '#cbd5e1',
                opacity: 1,
              },
            }}
          />
        </Box>

        {/* Results Info */}
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500 }}>
            <strong style={{ color: '#1e293b' }}>{filteredItems.length}</strong> of <strong style={{ color: '#1e293b' }}>{chemicalItems.length}</strong> chemicals
          </Typography>
        </Box>

        {/* Cards Grid */}
        {filteredItems.length > 0 ? (
          <Grid container spacing={3}>
            {filteredItems.map((item) => {
              return (
                <Grid item xs={12} sm={6} md={4} key={item.id}>
                  <Card
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      borderRadius: '12px',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      border: '1px solid #e8eef7',
                      backgroundColor: '#fff',
                      '&:hover': {
                        transform: 'translateY(-6px)',
                        boxShadow: '0 8px 20px rgba(0,0,0,0.1)',
                        borderColor: '#cbd5e1',
                      },
                      cursor: 'pointer',
                    }}
                  >
                    <CardActionArea
                      onClick={() => handleItemClick(item)}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'stretch',
                        height: '100%',
                      }}
                    >
                      <CardContent
                        sx={{
                          flex: 1,
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 1.5,
                          pb: 2,
                        }}
                      >
                        {/* Chemical Name - Main Heading */}
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Inventory2OutlinedIcon
                              sx={{ color: '#003883', fontSize: 19, flexShrink: 0 }}
                            />
                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: 700,
                                color: '#003883',
                                fontSize: '16px',
                                letterSpacing: 0,
                                overflowWrap: 'anywhere',
                              }}
                            >
                              {item.chemicalName}
                            </Typography>
                          </Box>
                          <Typography
                            variant="caption"
                            sx={{
                              color: '#64748b',
                              display: 'block',
                              lineHeight: 1.4,
                              fontSize: '13px',
                            }}
                          >
                            {item.productName}
                          </Typography>
                        </Box>

                        <Divider sx={{ my: 0.5, borderColor: '#e2e8f0' }} />

                        {/* Key Information Grid */}
                        <Stack spacing={1}>
                          <Box>
                            <Typography
                              variant="caption"
                              sx={{
                                color: '#94a3b8',
                                fontWeight: 600,
                                fontSize: '10px',
                                textTransform: 'uppercase',
                                letterSpacing: '0.4px',
                              }}
                            >
                              CAS Number
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                color: '#1e293b',
                                fontWeight: 600,
                                fontSize: '13px',
                                mt: 0.3,
                              }}
                            >
                              {item.casNo}
                            </Typography>
                          </Box>

                          <Box>
                            <Typography
                              variant="caption"
                              sx={{
                                color: '#94a3b8',
                                fontWeight: 600,
                                fontSize: '10px',
                                textTransform: 'uppercase',
                                letterSpacing: '0.4px',
                              }}
                            >
                              Classification
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                color: '#475569',
                                fontSize: '13px',
                                mt: 0.3,
                              }}
                            >
                              {item.pictogram}
                            </Typography>
                          </Box>
                        </Stack>

                        <Divider sx={{ my: 0.5, borderColor: '#e2e8f0' }} />

                        {/* Click to view details */}
                        <Typography
                          variant="caption"
                          sx={{
                            color: '#ee7d11',
                            fontWeight: 600,
                            fontSize: '12px',
                            pt: 0.5,
                            cursor: 'pointer',
                          }}
                        >
                          View details
                        </Typography>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        ) : (
          <Card
            sx={{
              borderRadius: '12px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
              backgroundColor: '#f8fafc',
              border: '1px solid #e2e8f0',
            }}
          >
            <CardContent sx={{ textAlign: 'center', py: 6 }}>
              <SearchIcon
                sx={{
                  fontSize: 48,
                  color: '#cbd5e1',
                  mb: 2,
                }}
              />
              <Typography variant="h6" sx={{ color: '#94a3b8', mb: 1, fontWeight: 600 }}>
                No chemicals found
              </Typography>
              <Typography variant="body2" sx={{ color: '#cbd5e1' }}>
                Try adjusting your search term
              </Typography>
            </CardContent>
          </Card>
        )}
      </Container>

      {/* Chemical Detail Modal */}
      <ChemicalDetailModal
        open={modalOpen}
        chemical={selectedChemical}
        onClose={handleCloseModal}
      />
    </Box>
  );
};
